// Helper utility functions

export const generateClubCode = () => {
  return Math.random().toString(36).substring(2, 8).toUpperCase();
};

export const generateId = (prefix) => {
  return `${prefix}_${Date.now()}`;
};

export const formatCurrency = (amount) => {
  return `$${parseFloat(amount).toFixed(2)}`;
};

// Constants
export const APP_TIMEZONE = 'America/Los_Angeles';

export const formatDate = (dateString, options = {}) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Invalid Date';

  // If input is YYYY-MM-DD (length 10), treat as UTC to avoid timezone shift
  const isDateOnly = typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  const timeZone = isDateOnly ? 'UTC' : APP_TIMEZONE;

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone,
    ...options
  });
};

export const formatDateTime = (dateString, options = {}) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Invalid Date';

  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: APP_TIMEZONE,
    ...options
  });
};

export const formatTimeFromDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';

  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: APP_TIMEZONE
  });
};

export const calculateExpensePerPlayer = (totalExpense, numberOfPlayers = 11) => {
  return totalExpense / numberOfPlayers;
};

export const readFileAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const validateFile = (file, maxSize = 5 * 1024 * 1024) => {
  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'Please upload an image file' };
  }
  if (file.size > maxSize) {
    return { valid: false, error: 'File size must be less than 5MB' };
  }
  return { valid: true };
};

/**
 * Formats a currency amount into a standard string representation
 * @param {number|string} amount - The amount to format
 * @returns {string} The formatted currency string
 */
export const formatCurrencyDetailed = (amount) => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(numAmount)) return '$0.00';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numAmount);
};

/**
 * Determines the current status of a game based on manual override or date.
 * Scheduled and Completed are automatic.
 * Cancelled and Abandoned are manual overrides.
 * 
 * @param {Object} game - The game document
 * @returns {string} 'Scheduled', 'Completed', 'Cancelled', or 'Abandoned'
 */
export const getGameStatus = (game) => {
  if (!game) return 'Scheduled';

  // 1. Check for manual overrides first
  if (game.status === 'Cancelled') return 'Cancelled';
  if (game.status === 'Abandoned') return 'Abandoned';
  if (game.status === 'Completed') return 'Completed';

  // 2. Automatically derive based on date
  if (game.date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let gameDate;
    const isDateOnly = typeof game.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(game.date);
    
    if (isDateOnly) {
      // Parse strictly in local time by splitting and constructing integer values
      const [year, month, day] = game.date.split('-');
      gameDate = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    } else {
      gameDate = new Date(game.date);
    }
    
    gameDate.setHours(0, 0, 0, 0);

    // If gameDate is strictly before today (meaning the day has ended)
    if (gameDate < today) {
      return 'Completed';
    }
  }

  // Default
  return 'Scheduled';
};

/**
 * Format time to readable string
 * @param {string} time - Time to format (HH:MM format)
 * @returns {string} Formatted time string
 */
export const formatTime = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const displayHour = h % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Calculate player's share of expense with super sub support
 * @param {number} totalAmount - Total expense amount
 * @param {number} regularPlayers - Number of regular players
 * @param {number} superSubs - Number of super subs (they pay half)
 * @param {boolean} isSuperSub - Is this player a super sub?
 * @returns {number} Player's share amount
 */
export const calculatePlayerShare = (totalAmount, regularPlayers, superSubs, isSuperSub = false) => {
  // Super subs pay half the cost of regular players
  const effectiveShares = regularPlayers + (superSubs / 2);
  const costPerShare = totalAmount / effectiveShares;
  return isSuperSub ? costPerShare / 2 : costPerShare;
};

/**
 * Returns the appropriate label for the initial registration fee based on club name
 * @param {Object|string} clubInfoOrName - The club document data or just the club name
 * @returns {string} The localized label
 */
export const getRegistrationFeeLabel = (clubInfoOrName) => {
  const name = typeof clubInfoOrName === 'string' ? clubInfoOrName : clubInfoOrName?.name;
  if (!name) return 'Initial Donation';
  
  const nameLower = name.toLowerCase();
  if (nameLower.includes('cascadia willow') || nameLower.includes('cwc')) {
    return 'Annual Membership Fee';
  }
  return 'Initial Donation';
};
